import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Info, MessageCircle, Mail } from 'lucide-react';
import { theme } from '../../../styles/theme';
import { ACTIONS } from '../../../hooks/useWizardForm';
import StepHint from '../StepHint';

export default function StepBasic({ state, dispatch, errors, agents, connectors }) {
  const { t } = useTranslation('campaigns');
  const handleNameChange = (e) => {
    dispatch({ type: ACTIONS.SET_FIELD, payload: { path: 'name', value: e.target.value } });
  };

  const handleAgentChange = (e) => {
    const newInstanceId = e.target.value;
    dispatch({ type: ACTIONS.SET_FIELD, payload: { path: 'instanceId', value: newInstanceId } });
    // Clear channel selection when agent changes — channel availability depends on agent connectors
    if (newInstanceId !== state.instanceId) {
      dispatch({ type: ACTIONS.SELECT_CHANNEL, payload: { channel: '', connectorId: '' } });
    }
  };

  // Filter connectors by agent instance and channel type
  // Only connectors linked to the selected agent are considered available
  // enabled may be undefined (legacy) — treat as enabled unless explicitly false
  const getChannelConnectors = (type) =>
    connectors.filter((c) => c.enabled !== false && c.type === type && c.agentInstanceId === state.instanceId);

  const hasWhatsapp = getChannelConnectors('whatsapp').length > 0;
  const hasEmail = getChannelConnectors('email').length > 0;

  const handleChannelSelect = (channel) => {
    const chConnectors = getChannelConnectors(channel);
    const autoConnectorId = chConnectors.length >= 1 ? chConnectors[0].connectorId : '';
    // eslint-disable-next-line no-console
    console.log('[StepBasic] channel select:', {
      channel, instanceId: state.instanceId,
      foundConnectors: chConnectors.length,
      autoConnectorId,
      connectorDetails: chConnectors.map(c => ({ connectorId: c.connectorId, type: c.type, agentInstanceId: c.agentInstanceId, businessAccountId: c.businessAccountId })),
    });
    dispatch({
      type: ACTIONS.SELECT_CHANNEL,
      payload: { channel, connectorId: autoConnectorId },
    });
  };

  const selectedConnector = connectors.find((c) => c.connectorId === state.connectorId);

  return (
    <Container>
      <StepHint
        icon={Info}
        title={t('stepBasic.hintTitle')}
        description={t('stepBasic.hintDescription')}
      />

      <FieldGroup>
        <Label htmlFor="campaign-name">{t('stepBasic.name')}</Label>
        <Input
          id="campaign-name"
          type="text"
          value={state.name}
          onChange={handleNameChange}
          placeholder={t('stepBasic.namePlaceholder')}
          $hasError={!!errors.name}
          aria-invalid={!!errors.name}
          aria-describedby={errors.name ? 'name-error' : undefined}
        />
        {errors.name && <ErrorMessage id="name-error" role="alert">{errors.name}</ErrorMessage>}
      </FieldGroup>

      <FieldGroup>
        <Label htmlFor="agent-select">{t('stepBasic.agent')}</Label>
        <Select
          id="agent-select"
          value={state.instanceId}
          onChange={handleAgentChange}
          $hasError={!!errors.instanceId}
          aria-invalid={!!errors.instanceId}
          aria-describedby={errors.instanceId ? 'agent-error' : undefined}
        >
          <option value="">{t('stepBasic.selectAgent')}</option>
          {agents.map((agent) => (
            <option key={agent.instanceId} value={agent.instanceId}>
              {agent.name}
            </option>
          ))}
        </Select>
        {errors.instanceId && <ErrorMessage id="agent-error" role="alert">{errors.instanceId}</ErrorMessage>}
      </FieldGroup>

      <FieldGroup>
        <Label>{t('stepBasic.channel')}</Label>
        <ChannelOptions>
          <ChannelCard
            type="button"
            $selected={state.channel === 'whatsapp'}
            $hasError={!!errors.channel && !state.channel}
            $disabled={!hasWhatsapp}
            onClick={() => hasWhatsapp && handleChannelSelect('whatsapp')}
            aria-pressed={state.channel === 'whatsapp'}
            aria-disabled={!hasWhatsapp}
            title={!hasWhatsapp ? t('stepBasic.noWhatsappConnector') : ''}
          >
            <ChannelIcon $color={theme.colors.channels.whatsapp}>
              <MessageCircle size={20} />
            </ChannelIcon>
            <ChannelLabel>WhatsApp</ChannelLabel>
            {!hasWhatsapp && <DisabledHint>{t('stepBasic.noConnector')}</DisabledHint>}
          </ChannelCard>

          <ChannelCard
            type="button"
            $selected={state.channel === 'email'}
            $hasError={!!errors.channel && !state.channel}
            $disabled={!hasEmail}
            onClick={() => hasEmail && handleChannelSelect('email')}
            aria-pressed={state.channel === 'email'}
            aria-disabled={!hasEmail}
            title={!hasEmail ? t('stepBasic.noEmailConnector') : ''}
          >
            <ChannelIcon $color={theme.colors.channels.email}>
              <Mail size={20} />
            </ChannelIcon>
            <ChannelLabel>{t('stepBasic.email')}</ChannelLabel>
            {!hasEmail && <DisabledHint>{t('stepBasic.noConnector')}</DisabledHint>}
          </ChannelCard>
        </ChannelOptions>
        {errors.channel && <ErrorMessage role="alert">{errors.channel}</ErrorMessage>}
      </FieldGroup>

      {state.channel && selectedConnector && (
        <ConnectorInfo>
          <ConnectorLabel>{t('stepBasic.connector')}</ConnectorLabel>
          <ConnectorName>{selectedConnector.name}</ConnectorName>
        </ConnectorInfo>
      )}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[5]};
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
`;

const Input = styled.input`
  width: 100%;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${(props) => (props.$hasError ? theme.colors.danger : theme.colors.border)};
  border-radius: ${theme.radii.md};
  font-size: ${theme.typography.sm};
  font-family: ${theme.typography.fontFamily};
  color: ${theme.colors.textPrimary};
  background: ${theme.colors.white};
  transition: ${theme.transitions.fast};

  &::placeholder {
    color: ${theme.colors.textMuted};
  }

  &:hover {
    border-color: ${(props) => (props.$hasError ? theme.colors.danger : theme.colors.borderHover)};
  }

  &:focus {
    outline: none;
    border-color: ${(props) => (props.$hasError ? theme.colors.danger : theme.colors.primary)};
    box-shadow: 0 0 0 3px ${(props) => (props.$hasError ? theme.colors.dangerLight : theme.colors.primaryLight)};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${(props) => (props.$hasError ? theme.colors.danger : theme.colors.border)};
  border-radius: ${theme.radii.md};
  font-size: ${theme.typography.sm};
  font-family: ${theme.typography.fontFamily};
  color: ${theme.colors.textPrimary};
  background: ${theme.colors.white};
  transition: ${theme.transitions.fast};
  cursor: pointer;
  appearance: auto;

  &:hover {
    border-color: ${(props) => (props.$hasError ? theme.colors.danger : theme.colors.borderHover)};
  }

  &:focus {
    outline: none;
    border-color: ${(props) => (props.$hasError ? theme.colors.danger : theme.colors.primary)};
    box-shadow: 0 0 0 3px ${(props) => (props.$hasError ? theme.colors.dangerLight : theme.colors.primaryLight)};
  }
`;

const ChannelOptions = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
`;

const ChannelCard = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  flex: 1;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 2px solid ${(props) => {
    if (props.$selected) return theme.colors.primary;
    if (props.$hasError) return theme.colors.danger;
    return theme.colors.border;
  }};
  border-radius: ${theme.radii.lg};
  background: ${(props) => (props.$selected ? theme.colors.primaryLight : theme.colors.white)};
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  transition: ${theme.transitions.fast};

  &:hover {
    border-color: ${(props) => {
      if (props.$disabled) return theme.colors.border;
      return props.$selected ? theme.colors.primary : theme.colors.borderHover;
    }};
    background: ${(props) => {
      if (props.$disabled) return theme.colors.white;
      return props.$selected ? theme.colors.primaryLight : theme.colors.gray50;
    }};
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
  }
`;

const ChannelIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.$color || theme.colors.textSecondary};
`;

const ChannelLabel = styled.span`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.medium};
  color: ${theme.colors.textPrimary};
`;

const ErrorMessage = styled.div`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.danger};
`;

const ConnectorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.bgSecondary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
`;

const ConnectorLabel = styled.span`
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.medium};
  color: ${theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ConnectorName = styled.span`
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textPrimary};
  font-weight: ${theme.typography.medium};
`;

const DisabledHint = styled.span`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
  font-style: italic;
`;
